import {html, HTMLTemplateResult, nothing} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {keyed} from "lit/directives/keyed.js";
import {repeat} from "lit/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {translate} from "lit-translate";
import {takeUntil} from "rxjs/operators";
import {container} from "tsyringe";

import {AbstractLitElement} from "../../components/abstract-lit.element";
import "../../components/dashboard-creator/dashboard-creator.component";
import {acquireLinkviewEditLock} from "../../domain/helpers/acquire-linkview-edit-lock.helper";
import {AbstractLinkviewRepository, LINKVIEW_REPOSITORY} from "../../domain/repositories/abstract-linkview.repository";
import type {Linkview} from "../../domain/entities/linkview.entity";

/** One button of the board toolbar: a stable key plus its template. */
interface LinkviewToolbarItem {
    readonly id: string;
    readonly render: () => HTMLTemplateResult;
    /** `'edit'`: Bearbeiten / Speichern / Abbrechen — rendered together in ONE
     *  `<div class="button-group">`; items without a group sit directly in the
     *  toolbar. */
    readonly group?: 'edit';
}

/**
 * Renders ONE toolbar button behind an error boundary — the very same
 * semantics as `ToolbarHelper.renderTimelineToolbar`'s own `safeRender`, which
 * this view lost when it started building its toolbar by hand (it needs a
 * `button-group` wrapper the helper does not offer, see `_renderToolbar`).
 *
 * Without it ONE throwing button template takes the WHOLE board view down with
 * it: `render()` throws, Lit leaves the view unrendered, and the user loses the
 * board over a broken button. With it the failure is logged with the item's id
 * and only that one button is missing; every other button and the board itself
 * keep drawing.
 */
function safeRenderToolbarItem(item: LinkviewToolbarItem): HTMLTemplateResult | typeof nothing {
    try {
        return item.render();
    } catch (error) {
        console.error(`Toolbar item "${item.id}" failed to render:`, error);
        return nothing;
    }
}

/**
 * Top-level Linkview mode landing view.
 *
 * Routes `/linkview` (bare list, no Linkview selected) AND
 * `/linkview/:sid/:slug?` (one named Linkview) — same `<o2d-linkview-view>`
 * element with a one-way `.sid=` binding for both, mirroring
 * `knowledge-overview-view`'s `/knowledge` + `/knowledge/:sid/:slug?`
 * pattern (routes.ts). Mode-switch entry: ha-mode-switch.component.ts, mode
 * derivation: mode-router.controller.ts.
 *
 * Anke, 2026-09-04: "es kann mehrere Linkviews geben" — the actual list +
 * "Neu" button live in `<linkview-sidebar-list>` (`ha-app.component.ts`,
 * persistent sidebar, same spot `<knowledge-sidebar-list>` occupies for
 * Wissen mode), not here.
 *
 * Anke, 2026-09-17: "die Linkboards auf den LOCK-mechanismus umstellen, wie
 * die Whiteboards ... oben eine toolbar (bitte strukturell an todo
 * orientieren) mit einer bearbeiten-taste ... damit entfallen die
 * schloss-taste und die plus-taste am linkbaord in der sidebar." — this
 * view now ALSO renders the board's toolbar (its own `<div
 * class="timeline-toolbar">` with the buttons DIRECTLY inside, no
 * `timeline-toolbar-start`/`-end` wrapper divs — user, 2026-09-19 — unlike
 * `ToolbarHelper.renderTimelineToolbar` that `ha-tasklist.view.ts` and the
 * timeline views share; not the Whiteboard's `@o2d/whiteboard`-owned
 * `InjectedButton` mechanism — `dashboard-creator` is a horstplan-native
 * Gridstack component with no toolbar of its own to inject into, see its
 * own class doc comment) — but WITH that helper's error boundary kept, see
 * `safeRenderToolbarItem`): "Bearbeiten" while LOCKED; "Speichern" /
 * "Abbrechen" / "+ Neue Kachel" (in that order) while UNLOCKED. The toolbar and
 * `<dashboard-creator>` are DOM siblings, not parent/child — every button
 * click reaches the right mounted `dashboard-creator` instance through
 * `AbstractLinkviewRepository`'s `items$`/`addTileRequested$`/
 * `saveRequested$`/`cancelRequested$`, exactly the same coordination
 * mechanism the sidebar's now-removed lock/add-tile buttons used to.
 *
 * `keyed(this.sid, ...)`: `dashboard-creator` does its own Gridstack
 * initialization once in `firstUpdated()` — reusing the SAME instance across
 * two different Linkviews (as the reconciled-not-remounted `.sid=` binding
 * would otherwise do) would leave it wired to the FIRST Linkview's grid/
 * storage key. Keying by `sid` forces Lit to tear down and recreate the
 * element whenever the routed Linkview changes, so it always re-initializes
 * fresh for the new one. The toolbar itself needs no such keying — it reads
 * `_items`/`sid` fresh on every render, no per-board internal state of its
 * own to go stale.
 *
 * Light DOM is inherited from AbstractLitElement (ADR-007).
 */
@customElement('o2d-linkview-view')
export class LinkviewView extends AbstractLitElement {

    /** From the `/linkview/:sid/:slug?` route param. `undefined` on the bare
     *  `/linkview` list — see `keyed()`'s doc comment above on why this is
     *  the value `<dashboard-creator>` is re-keyed by, not just bound to. */
    @property({type: String})
    public sid?: string;

    @state()
    private _items: Linkview[] = [];

    private readonly _repo: AbstractLinkviewRepository = container.resolve(LINKVIEW_REPOSITORY);

    /** Re-entrancy guard for the toolbar's "Bearbeiten" button — the
     *  acquire-or-take-over flow awaits at least one thing (the lock call,
     *  possibly a confirm dialog) before `items$` reflects the new holder,
     *  so a rapid double-click could otherwise fire it twice. */
    private _editing = false;

    public override connectedCallback(): void {
        super.connectedCallback();

        this._repo.items$
            .pipe(takeUntil(this._disconnected$))
            .subscribe((items) => this._items = items);
    }

    private get _currentItem(): Linkview | undefined {
        return this._items.find((item) => item.uid === this.sid);
    }

    public render(): HTMLTemplateResult {
        return html`
            ${when(
                this.sid,
                () => html`
                    ${this._renderToolbar()}
                    ${keyed(this.sid, html`
                        <dashboard-creator .linkviewUid=${this.sid}></dashboard-creator>
                    `)}
                `,
                () => html`
                    <div class="linkview-empty-state">
                        ${translate('linkview.view.empty-state')}
                    </div>
                `,
            )}
        `;
    }

    private _renderToolbar(): HTMLTemplateResult | typeof nothing {
        const item = this._currentItem;
        if (!item) {
            return nothing;
        }

        const items = this._buildToolbarItems(item);
        const editGroup = items.filter((toolbarItem) => toolbarItem.group === 'edit');
        const others = items.filter((toolbarItem) => toolbarItem.group !== 'edit');

        return html`
            <div class="timeline-toolbar">
                <div class="button-group">
                    ${repeat(editGroup, (toolbarItem) => toolbarItem.id, (toolbarItem) => safeRenderToolbarItem(toolbarItem))}
                </div>
                ${repeat(others, (toolbarItem) => toolbarItem.id, (toolbarItem) => safeRenderToolbarItem(toolbarItem))}
            </div>
        `;
    }

    /** Left to right: Bearbeiten (locked) — or Speichern, Abbrechen, Neue
     *  Kachel (unlocked). Bearbeiten/Speichern + Abbrechen form the `edit`
     *  group (one `button-group` div). The order here IS the visual order; the row
     *  direction is set in linkview-toolbar.css (`o2d-linkview-view .timeline-toolbar`). */
    private _buildToolbarItems(item: Linkview): LinkviewToolbarItem[] {
        if (item.locked) {
            return [{
                id: 'edit',
                group: 'edit',
                render: () => html`
                    <button type="button"
                            class="edit buttonform-marker"
                            title=${translate('linkview.board.edit')}
                            aria-label=${translate('linkview.board.edit')}
                            data-label=${translate('linkview.board.edit')}
                            @click=${this._onClickEdit}
                    ><div class="hover-effect"></div></button>
                `,
            }];
        }

        return [
            {
                id: 'save',
                group: 'edit',
                render: () => html`
                    <button type="button"
                            class="save buttonform-marker primary"
                            title=${translate('linkview.board.save')}
                            aria-label=${translate('linkview.board.save')}
                            data-label=${translate('linkview.board.save')}
                            @click=${() => this._repo.requestSave(item.uid)}
                    ><div class="hover-effect"></div></button>
                `,
            },
            {
                id: 'cancel',
                group: 'edit',
                render: () => html`
                    <button type="button"
                            class="cancel buttonform-marker secondary"
                            title=${translate('linkview.board.cancel')}
                            aria-label=${translate('linkview.board.cancel')}
                            data-label=${translate('linkview.board.cancel')}
                            @click=${() => this._repo.requestCancel(item.uid)}
                    ><div class="hover-effect"></div></button>
                `,
            },
            {
                id: 'add-tile',
                render: () => html`
                    <button type="button"
                            class="add-linktile buttonform-marker secondary"
                            title=${translate('linkview.board.new-tile')}
                            aria-label=${translate('linkview.board.new-tile')}
                            data-label=${translate('linkview.board.new-tile')}
                            @click=${() => this._repo.requestAddTile(item.uid)}
                    ><div class="hover-effect"></div></button>
                `,
            },
        ];
    }

    private _onClickEdit = async (): Promise<void> => {
        const item = this._currentItem;
        if (!item || this._editing) {
            return;
        }
        this._editing = true;
        try {
            await acquireLinkviewEditLock(item);
        } finally {
            this._editing = false;
        }
    };

}
